import { useState, useRef, useEffect, useMemo, Fragment, useCallback } from 'react';
import {
    DndContext,
    pointerWithin,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
} from '@dnd-kit/sortable';
import useFileStore from '../../stores/fileStore';
import { isValidName } from '../../utils/fileSystem';
import { useTranslation } from 'react-i18next';
import {
    hasSiblingNameConflict,
    isProtectedProjectPath,
    joinProjectFilePath,
} from '../../../shared/projectFileSystem.ts';
import { getSystemFolderLabel } from '../../utils/i18nHelpers';
import './FileTree.css';

// pointerWithin for folder inside/edge zones, closestCenter fallback so a target always exists
const treeCollisionDetection = (args) => {
    const hits = pointerWithin(args);
    return hits.length > 0 ? hits : closestCenter(args);
};

const sortByOrder = (items) =>
    [...items].sort((a, b) => {
        const oa = typeof a.order === 'number' ? a.order : Number.MAX_SAFE_INTEGER;
        const ob = typeof b.order === 'number' ? b.order : Number.MAX_SAFE_INTEGER;
        return oa !== ob ? oa - ob : a.name.localeCompare(b.name);
    });

const normalizeSelectedPaths = (paths) => {
    const unique = [...new Set(paths)];
    return unique.filter(
        (p) => !unique.some((o) => o !== p && p.startsWith(`${o}/`))
    );
};

function FileTree({ projectId, onMobileFileOpen }) {
    const { t } = useTranslation();
    const {
        getFileTree, deleteItems, toggleFolder, isFolderExpanded,
        openFile: _openFile, activeTabPath, files, createNewFile,
        renameItem, moveItems, reorderItems,
    } = useFileStore();

    // Use mobile callback if provided (switches to editor panel after opening)
    const openFile = onMobileFileOpen || _openFile;

    const [contextMenu, setContextMenu] = useState(null);
    const [renaming, setRenaming] = useState(null);
    const [creating, setCreating] = useState(null);
    const [draggedItem, setDraggedItem] = useState(null);
    const [dropIndicator, setDropIndicator] = useState(null);
    const [selectedPaths, setSelectedPaths] = useState([]);
    const [selectionAnchor, setSelectionAnchor] = useState(null);
    const draggedPathsRef = useRef([]);
    const dropIndicatorRef = useRef(null);
    const expandTimerRef = useRef(null);
    const hoveredFolderRef = useRef(null);

    const tree = getFileTree();
    const fileMap = useMemo(() => new Map(files.map((f) => [f.path, f])), [files]);
    const selectedPathSet = useMemo(() => new Set(selectedPaths), [selectedPaths]);
    const showConflictMessage = useCallback(() => {
        window.alert(t('sidebar.conflictMessage'));
    }, [t]);
    const showProtectedPathMessage = useCallback((path) => {
        window.alert(t('sidebar.protectedPathMessage', { name: path.split('/').pop() }));
    }, [t]);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    // Close context menu on outside click
    useEffect(() => {
        const h = () => setContextMenu(null);
        document.addEventListener('click', h);
        return () => document.removeEventListener('click', h);
    }, []);

    // Sync selection with available files
    useEffect(() => {
        const valid = new Set(files.map((f) => f.path));
        setSelectedPaths((cur) => cur.filter((p) => valid.has(p)));
        setSelectionAnchor((cur) => (cur && valid.has(cur) ? cur : null));
    }, [files]);

    // Reset on project change
    useEffect(() => {
        setSelectedPaths([]);
        setSelectionAnchor(null);
        draggedPathsRef.current = [];
        setDraggedItem(null);
        setContextMenu(null);
        setDropIndicator(null);
        dropIndicatorRef.current = null;
    }, [projectId]);

    const clearHoverExpand = useCallback(() => {
        if (expandTimerRef.current) clearTimeout(expandTimerRef.current);
        expandTimerRef.current = null;
        hoveredFolderRef.current = null;
    }, []);

    useEffect(() => () => clearHoverExpand(), [clearHoverExpand]);

    // ── Tree flattening ──

    const flattenTree = useCallback((items, level = 0) => {
        let result = [];
        for (const item of items) {
            result.push({ ...item, level });
            if (item.type === 'folder' && item.children && isFolderExpanded(item.path)) {
                result = result.concat(flattenTree(item.children, level + 1));
            }
        }
        return result;
    }, [isFolderExpanded]);

    const flatItems = useMemo(() => flattenTree(tree), [tree, flattenTree]);
    const flatIndexMap = useMemo(
        () => new Map(flatItems.map((item, i) => [item.path, i])),
        [flatItems]
    );

    const creationPlacement = useMemo(() => {
        if (!creating || !creating.parentPath) return null;
        const pi = flatItems.findIndex((item) => item.path === creating.parentPath);
        if (pi < 0) return null;
        let insertIndex = pi;
        for (let i = pi + 1; i < flatItems.length; i++) {
            if (flatItems[i].path.startsWith(`${creating.parentPath}/`)) insertIndex = i;
            else break;
        }
        return { insertIndex, level: (flatItems[pi]?.level ?? 0) + 1 };
    }, [creating, flatItems]);

    // ── Selection ──

    const clearSelection = useCallback(() => {
        setSelectedPaths([]);
        setSelectionAnchor(null);
    }, []);

    const selectRange = useCallback((targetPath, additive) => {
        if (!selectionAnchor || !flatIndexMap.has(selectionAnchor) || !flatIndexMap.has(targetPath)) {
            setSelectedPaths([targetPath]);
            setSelectionAnchor(targetPath);
            return;
        }
        const s = flatIndexMap.get(selectionAnchor);
        const e = flatIndexMap.get(targetPath);
        const [from, to] = s < e ? [s, e] : [e, s];
        const range = flatItems.slice(from, to + 1).map((i) => i.path);
        setSelectedPaths((cur) => additive ? [...new Set([...cur, ...range])] : range);
    }, [selectionAnchor, flatIndexMap, flatItems]);

    const handleItemClick = (event, item, { toggleOnly = false } = {}) => {
        event.stopPropagation();
        const mod = event.metaKey || event.ctrlKey;
        if (event.shiftKey) {
            selectRange(item.path, mod);
        } else if (mod) {
            setSelectedPaths((cur) =>
                cur.includes(item.path) ? cur.filter((p) => p !== item.path) : [...cur, item.path]
            );
            setSelectionAnchor(item.path);
        } else {
            setSelectedPaths([item.path]);
            setSelectionAnchor(item.path);
        }
        if (toggleOnly || event.shiftKey || mod) return;
        if (item.type === 'folder') toggleFolder(item.path);
        else openFile(item.path);
    };

    // ── Context menu actions ──

    const handleContextMenu = (e, item) => {
        e.preventDefault();
        e.stopPropagation();
        if (!selectedPathSet.has(item.path)) {
            setSelectedPaths([item.path]);
            setSelectionAnchor(item.path);
        }
        setContextMenu({ x: e.clientX, y: e.clientY, item });
    };

    const handleRootContextMenu = (e) => {
        e.preventDefault();
        setContextMenu({ x: e.clientX, y: e.clientY, item: null });
    };

    const handleCreate = async (type) => {
        const effectiveType = type === 'canvas' ? 'file' : type;
        let parentPath = '';
        if (contextMenu?.item) {
            if (effectiveType === 'folder') {
                parentPath = '';
            } else if (contextMenu.item.type === 'folder') {
                parentPath = contextMenu.item.path;
                if (!isFolderExpanded(contextMenu.item.path)) toggleFolder(contextMenu.item.path);
            } else {
                parentPath = contextMenu.item.parentPath || '';
            }
        }
        setCreating({ parentPath, type: effectiveType, isCanvas: type === 'canvas' });
        setContextMenu(null);
    };

    const handleCreateSubmit = async (name) => {
        if (!name || !isValidName(name)) { setCreating(null); return; }
        let ext;
        if (creating.isCanvas) {
            ext = !name.endsWith('.canvas') ? '.canvas' : '';
        } else {
            ext = creating.type === 'file' && !name.includes('.') ? '.md' : '';
        }
        const nextName = name + ext;
        if (hasSiblingNameConflict(files, creating.parentPath, nextName)) {
            showConflictMessage();
            return;
        }
        await createNewFile(projectId, creating.parentPath, nextName, creating.type);
        setCreating(null);
    };

    const handleDelete = async () => {
        const selectedMovePaths = normalizeSelectedPaths(selectedPaths.filter((p) => fileMap.has(p)));
        const targets =
            contextMenu?.item && selectedPathSet.has(contextMenu.item.path)
                ? selectedMovePaths
                : contextMenu?.item ? [contextMenu.item.path] : [];
        if (targets.length > 0) {
            const protectedPath = targets.find((path) => isProtectedProjectPath(path));
            if (protectedPath) {
                showProtectedPathMessage(protectedPath);
                setContextMenu(null);
                return;
            }
            const confirmationMessage = targets.length === 1
                ? t('sidebar.deleteItemConfirm', { name: targets[0].split('/').pop() })
                : t('sidebar.deleteItemsConfirm', { count: targets.length });
            if (window.confirm(confirmationMessage)) {
                await deleteItems(projectId, targets);
                clearSelection();
            }
        }
        setContextMenu(null);
    };

    const handleRename = () => {
        if (contextMenu?.item?.path && isProtectedProjectPath(contextMenu.item.path)) {
            showProtectedPathMessage(contextMenu.item.path);
            setContextMenu(null);
            return;
        }
        setRenaming(contextMenu?.item?.path);
        setContextMenu(null);
    };

    const handleRenameSubmit = async (path, newName) => {
        if (!isValidName(newName)) { setRenaming(null); return; }
        if (isProtectedProjectPath(path)) {
            showProtectedPathMessage(path);
            setRenaming(null);
            return;
        }
        const file = fileMap.get(path);
        if (!file) {
            setRenaming(null);
            return;
        }
        const nextPath = joinProjectFilePath(file.parentPath || '', newName);
        if (nextPath !== path && hasSiblingNameConflict(files, file.parentPath || '', newName, path)) {
            showConflictMessage();
            return;
        }
        await renameItem(projectId, path, newName);
        setRenaming(null);
    };

    // ── Sort folder ──

    const handleSortFolder = useCallback(async (folderPath) => {
        const children = files
            .filter((f) => (f.parentPath || '') === folderPath)
            .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));
        if (children.length < 2) return;
        const orderedPaths = children.map((f) => f.path);
        console.log('[Sort]', folderPath, orderedPaths);
        await reorderItems(projectId, folderPath, orderedPaths);
    }, [files, projectId, reorderItems]);

    // ── Drag and Drop ──

    const handleDragStart = (event) => {
        const path = String(event.active.id);
        const item = fileMap.get(path);
        if (!item) return;

        const paths = selectedPathSet.has(path) ? normalizeSelectedPaths(selectedPaths) : [path];
        if (!selectedPathSet.has(path)) {
            setSelectedPaths([path]);
            setSelectionAnchor(path);
        }
        draggedPathsRef.current = paths;
        setDraggedItem({ ...item, count: paths.length });
        setDropIndicator(null);
        dropIndicatorRef.current = null;
        clearHoverExpand();
    };

    const handleDragOver = (event) => {
        const { active, over } = event;
        if (!active || !over) {
            setDropIndicator(null);
            clearHoverExpand();
            return;
        }

        const activeRect = active.rect.current.translated || active.rect.current.initial;
        const overRect = over.rect;
        if (!activeRect || !overRect) {
            setDropIndicator(null);
            clearHoverExpand();
            return;
        }

        const overId = String(over.id);
        const overFile = fileMap.get(overId);
        const activePaths = draggedPathsRef.current.length > 0
            ? draggedPathsRef.current
            : [String(active.id)];
        const activeItems = normalizeSelectedPaths(activePaths)
            .map((path) => fileMap.get(path))
            .filter(Boolean);
        const draggingFolder = activeItems.some((item) => item.type === 'folder');
        const pointerY = activeRect.top + activeRect.height / 2;
        let position = 'after';

        if (overFile?.type === 'folder') {
            const offsetY = pointerY - overRect.top;
            const third = overRect.height / 3;
            if (offsetY <= third) position = 'before';
            else if (offsetY >= overRect.height - third) position = 'after';
            else position = draggingFolder ? 'after' : 'inside';
        } else {
            position = pointerY < overRect.top + overRect.height / 2 ? 'before' : 'after';
        }

        setDropIndicator({ id: overId, position });
        dropIndicatorRef.current = { id: overId, position };

        // Auto-expand collapsed folders on hover
        if (!draggingFolder && overFile?.type === 'folder' && position === 'inside' && !isFolderExpanded(overFile.path)) {
            if (hoveredFolderRef.current !== overFile.path) {
                clearHoverExpand();
                hoveredFolderRef.current = overFile.path;
                expandTimerRef.current = setTimeout(() => {
                    toggleFolder(overFile.path, true);
                    clearHoverExpand();
                }, 450);
            }
        } else if (hoveredFolderRef.current && hoveredFolderRef.current !== overFile?.path) {
            clearHoverExpand();
        }
    };

    const handleDragEnd = async (event) => {
        // 1. Capture refs before cleanup
        const { over } = event;
        const paths = draggedPathsRef.current.length > 0
            ? [...draggedPathsRef.current]
            : [String(event.active?.id)];
        const savedIndicator = dropIndicatorRef.current;

        // 2. Cleanup immediately
        setDraggedItem(null);
        draggedPathsRef.current = [];
        setDropIndicator(null);
        dropIndicatorRef.current = null;
        clearHoverExpand();

        // 3. Validate drop
        if (!over) return;
        const targetId = String(over.id);
        const target = fileMap.get(targetId);
        if (!target) return;

        const dragPaths = normalizeSelectedPaths(paths);
        const dragItems = dragPaths.map((p) => fileMap.get(p)).filter(Boolean);
        if (dragItems.length === 0) return;

        // Prevent dropping onto self or into own children
        if (dragItems.some((i) => i.path === target.path || target.path.startsWith(`${i.path}/`))) return;

        // 4. Determine indicator position (only trust if it matches the target)
        const pos = savedIndicator?.id === targetId ? savedIndicator.position : null;

        // 5. Determine source and destination parents
        const srcParent = dragItems[0].parentPath || '';
        const targetParent = target.parentPath || '';

        // 6a. REORDER — same parent, not dropping "inside" a folder
        if (srcParent === targetParent && pos !== 'inside') {
            const allSiblings = sortByOrder(
                files.filter((f) => (f.parentPath || '') === srcParent)
            );
            const dragSet = new Set(dragPaths);
            const selected = allSiblings.map((f) => f.path).filter((p) => dragSet.has(p));
            const remaining = allSiblings.map((f) => f.path).filter((p) => !dragSet.has(p));
            const idx = remaining.indexOf(target.path);
            if (idx !== -1) {
                const insertAt = Math.min(
                    pos === 'after' ? idx + 1 : idx,
                    remaining.length
                );
                remaining.splice(insertAt, 0, ...selected);
                await reorderItems(projectId, srcParent, remaining);
                return;
            }
        }

        // 6b. MOVE — different parent, or dropping "inside" a folder
        const destParent = (target.type === 'folder' && pos !== 'before' && pos !== 'after')
            ? target.path
            : targetParent;

        if (dragItems.some((item) => item.type === 'folder') && destParent) {
            return;
        }

        const protectedPath = dragItems.find((item) => isProtectedProjectPath(item.path));
        if (protectedPath && destParent !== srcParent) {
            showProtectedPathMessage(protectedPath.path);
            return;
        }

        // Don't move if already at destination
        if (destParent === srcParent) return;

        const mappings = await moveItems(projectId, dragPaths, destParent);
        if (mappings.length === 0) return;

        const newPaths = mappings.map((m) => m.to);
        setSelectedPaths(newPaths);
        setSelectionAnchor(newPaths[0]);

        // Place moved items at the drop position, not the bottom
        if (pos === 'before' || pos === 'after') {
            const freshFiles = useFileStore.getState().files;
            const siblings = sortByOrder(
                freshFiles.filter((f) => (f.parentPath || '') === destParent)
            );
            const movedSet = new Set(newPaths);
            const moved = siblings.map((f) => f.path).filter((p) => movedSet.has(p));
            const rest = siblings.map((f) => f.path).filter((p) => !movedSet.has(p));
            const idx = rest.indexOf(target.path);
            if (idx !== -1) {
                const insertAt = Math.min(pos === 'after' ? idx + 1 : idx, rest.length);
                rest.splice(insertAt, 0, ...moved);
                await reorderItems(projectId, destParent, rest);
            }
        }
    };

    // ── Render ──

    const contextItemProtected = contextMenu?.item ? isProtectedProjectPath(contextMenu.item.path) : false;

    return (
        <div className="file-tree">
          <div className="file-tree-scroll" onContextMenu={handleRootContextMenu}
            onClick={(e) => { if (e.target === e.currentTarget) clearSelection(); }}>
            <DndContext
                sensors={sensors}
                collisionDetection={treeCollisionDetection}
                screenReaderInstructions={{
                    draggable: t('sidebar.dragInstructions'),
                }}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
            >
                <SortableContext items={flatItems.map((i) => i.path)}>
                    <div className="tree-content">
                        {flatItems.length === 0 ? (
                            <div className="tree-empty">
                                <p>{t("sidebar.noFilesYet")}</p>
                                <p className="hint">{t("sidebar.rightClickToCreate")}</p>
                            </div>
                        ) : (
                            flatItems.map((item, index) => (
                                <Fragment key={item.path}>
                                    <SortableTreeItem
                                        item={item}
                                        level={item.level}
                                        onContextMenu={handleContextMenu}
                                        isExpanded={isFolderExpanded(item.path)}
                                        onClick={handleItemClick}
                                        activeTabPath={activeTabPath}
                                        renaming={renaming}
                                        onRenameSubmit={handleRenameSubmit}
                                        setRenaming={setRenaming}
                                        dropIndicator={dropIndicator}
                                        isSelected={selectedPathSet.has(item.path)}
                                        onSort={handleSortFolder}
                                    />
                                    {creating && creating.parentPath && creationPlacement &&
                                        index === creationPlacement.insertIndex && (
                                        <CreateInput
                                            type={creating.type}
                                            level={creationPlacement.level}
                                            onSubmit={handleCreateSubmit}
                                            onCancel={() => setCreating(null)}
                                        />
                                    )}
                                </Fragment>
                            ))
                        )}
                        {creating && creating.parentPath === '' && (
                            <CreateInput
                                type={creating.type}
                                level={0}
                                onSubmit={handleCreateSubmit}
                                onCancel={() => setCreating(null)}
                            />
                        )}
                    </div>
                </SortableContext>

                <DragOverlay>
                    {draggedItem && (
                        <div className="tree-item dragging drag-overlay">
                            <span className="item-icon">
                                {draggedItem.type === 'folder' ? <FolderIcon /> : <FileIcon />}
                            </span>
                            <span className="item-name">
                                {draggedItem.count > 1 ? t('sidebar.itemsCount', { count: draggedItem.count }) : getSystemFolderLabel(t, draggedItem.name)}
                            </span>
                        </div>
                    )}
                </DragOverlay>
            </DndContext>

          </div>

            <div className="tree-add-content">
                <button className="btn btn-ghost btn-sm"
                    onClick={(e) => {
                        e.stopPropagation();
                        e.nativeEvent.stopImmediatePropagation();
                        const rect = e.currentTarget.getBoundingClientRect();
                        const menuHeight = 130;
                        const x = Math.min(rect.left, window.innerWidth - 200);
                        const y = Math.max(8, rect.top - menuHeight);
                        setContextMenu({ x, y, item: null });
                    }}>
                    {t('sidebar.addContent')}
                </button>
            </div>

            {contextMenu && (
                <div className="context-menu"
                    style={{
                        left: Math.min(contextMenu.x, window.innerWidth - 200),
                        top: Math.min(contextMenu.y, window.innerHeight - 200),
                    }}
                    onClick={(e) => e.stopPropagation()}>
                    <div className="context-menu-item" onClick={() => handleCreate('file')}>
                        <span className="context-icon"><FileIcon /></span>{t('sidebar.newFile')}
                    </div>
                    <div className="context-menu-item" onClick={() => handleCreate('canvas')}>
                        <span className="context-icon">🎨</span>{t('sidebar.newCanvas')}
                    </div>
                    <div className="context-menu-item" onClick={() => handleCreate('folder')}>
                        <span className="context-icon"><FolderIcon /></span>{t('sidebar.newFolder')}
                    </div>
                    {contextMenu.item && (
                        <>
                            <div className="context-menu-separator" />
                            <div
                                className={`context-menu-item ${contextItemProtected ? 'disabled' : ''}`}
                                onClick={handleRename}
                                title={contextItemProtected ? t('sidebar.managedItemTitle') : undefined}
                            >
                                <span className="context-icon">✎</span>{t('common.rename')}
                            </div>
                            <div
                                className={`context-menu-item danger ${contextItemProtected ? 'disabled' : ''}`}
                                onClick={handleDelete}
                                title={contextItemProtected ? t('sidebar.managedItemTitle') : undefined}
                            >
                                <span className="context-icon">🗑︎</span>{t('common.delete')}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

function SortableTreeItem({
    item, level, onContextMenu, isExpanded, onClick,
    activeTabPath, renaming, onRenameSubmit, setRenaming,
    dropIndicator, isSelected, onSort,
}) {
    const { t } = useTranslation();
    const { attributes, listeners, setNodeRef, isDragging } = useSortable({ id: item.path });
    const isFolder = item.type === 'folder';
    const isActive = activeTabPath === item.path;
    const isRenaming = renaming === item.path;
    const childCount = isFolder && item.children ? item.children.length : 0;
    const indicatorPos = dropIndicator?.id === item.path ? dropIndicator.position : null;

    return (
        <div
            ref={setNodeRef}
            style={{ opacity: isDragging ? 0.4 : 1 }}
            className={[
                'tree-item',
                isActive && 'active',
                isSelected && 'selected',
                isFolder ? 'folder' : 'file',
                isDragging && 'dragging',
                level > 0 && 'nested',
                indicatorPos && `drop-${indicatorPos}`,
            ].filter(Boolean).join(' ')}
            onClick={(e) => onClick(e, item)}
            onContextMenu={(e) => onContextMenu(e, item)}
            {...attributes}
            {...listeners}
        >
            <div className="tree-item-content" style={{ paddingLeft: `${12 + level * 16}px` }}>
                {isFolder ? (
                    <button type="button"
                        className={`folder-arrow ${isExpanded ? 'expanded' : ''}`}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => { e.stopPropagation(); onClick(e, item, { toggleOnly: true }); }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="9 18 15 12 9 6" />
                        </svg>
                    </button>
                ) : (
                    <span className="folder-arrow placeholder" />
                )}
                <span className="item-icon">{isFolder ? <FolderIcon /> : <FileIcon />}</span>
                {isRenaming ? (
                    <RenameInput
                        initialValue={item.name}
                        onSubmit={(name) => onRenameSubmit(item.path, name)}
                        onCancel={() => setRenaming(null)}
                    />
                ) : (
                    <span className="item-name">{getSystemFolderLabel(t, item.name)}</span>
                )}
                {isFolder && childCount > 0 && !isExpanded && (
                    <span className="child-count">{childCount}</span>
                )}
                {isFolder && isExpanded && childCount > 1 && (
                    <button type="button" className="sort-button" title={t('sidebar.sortContents')}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => { e.stopPropagation(); onSort(item.path); }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                            <path d="M3 6h18M3 12h12M3 18h6" />
                        </svg>
                    </button>
                )}
            </div>
        </div>
    );
}

function CreateInput({ type, level, onSubmit, onCancel }) {
    const { t } = useTranslation();
    const [value, setValue] = useState('');
    const inputRef = useRef(null);
    useEffect(() => { inputRef.current?.focus(); }, []);

    return (
        <div className="tree-item creating">
            <div className="tree-item-content" style={{ paddingLeft: `${12 + level * 16}px` }}>
                <span className="folder-arrow placeholder" />
                <span className="item-icon">{type === 'folder' ? <FolderIcon /> : <FileIcon />}</span>
                <input ref={inputRef} type="text" className="tree-input" value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') onSubmit(value);
                        else if (e.key === 'Escape') onCancel();
                    }}
                    onBlur={onCancel}
                    placeholder={type === 'folder' ? t('sidebar.folderNamePlaceholder') : t('sidebar.fileNamePlaceholder')}
                />
            </div>
        </div>
    );
}

function RenameInput({ initialValue, onSubmit, onCancel }) {
    const { t } = useTranslation();
    const [value, setValue] = useState(initialValue);
    const inputRef = useRef(null);
    useEffect(() => { inputRef.current?.focus(); inputRef.current?.select(); }, []);

    return (
        <input ref={inputRef} type="text" className="tree-input" value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
                if (e.key === 'Enter') onSubmit(value);
                else if (e.key === 'Escape') onCancel();
            }}
            onBlur={onCancel}
            aria-label={t('common.rename')}
        />
    );
}

function FolderIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
            <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
        </svg>
    );
}

function FileIcon() {
    return (
        <svg width="14" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
            <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
            <path d="M14 3v6h6" />
        </svg>
    );
}

export default FileTree;
